import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Select } from '../../Form';
import Label from './Label/Label';
import StyledElement from '../../StyledElement';
import FocusableInput from './FormField/FocusableInput';
import { countries as allCountries } from '../../../lib/countries';
import { useCheckoutLocalization } from '../../../hooks/useCheckoutLocalization';
import { resolveAddressLocale, translateAddressKey } from '../../../lib/addressTranslations';

type Country = { alpha2: string; name: string };

export type LimitAllowedCountries = {
  enabled?: boolean;
  countries?: string[];
};

type CountrySelectProps = {
  id: string;
  value: string | undefined;
  onChange: (code: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  limitAllowedCountries?: LimitAllowedCountries;
  focusableInputId?: string;
  className?: string;
  ariaLabel?: string;
};

const selectClass = clsx('cp_form-select');

export default function CountrySelect({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  label,
  required,
  placeholder,
  limitAllowedCountries,
  focusableInputId,
  className,
  ariaLabel,
}: CountrySelectProps) {
  const { locale } = useCheckoutLocalization();
  const resolvedLocale = resolveAddressLocale(locale);

  const countries: Country[] = useMemo(() => {
    const base =
      limitAllowedCountries?.enabled && limitAllowedCountries.countries?.length
        ? allCountries.filter((c: Country) => limitAllowedCountries.countries!.includes(c.alpha2))
        : allCountries;
    if (resolvedLocale === 'en') return base;
    const displayNames = new Intl.DisplayNames([resolvedLocale], {
      type: 'region',
      fallback: 'none',
    });
    return base
      .map((c: Country) => ({ alpha2: c.alpha2, name: displayNames.of(c.alpha2) ?? c.name }))
      .sort((a: Country, b: Country) => a.name.localeCompare(b.name, resolvedLocale));
  }, [limitAllowedCountries, resolvedLocale]);

  const select = (
    <StyledElement
      elementPath="checkout.form.fields.input"
      as={Select}
      id={id}
      name={id}
      aria-label={ariaLabel ?? label ?? translateAddressKey(locale, 'ui.country')}
      required={required}
      value={value ?? ''}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      className={clsx(selectClass, className)}
    >
      {placeholder !== undefined && (
        <option value="" disabled={required} className="cp_form-option">
          {placeholder}
        </option>
      )}
      {countries.map(country => (
        <option
          key={country.alpha2}
          value={country.alpha2}
          className="cp_form-option flex justify-between"
          suppressHydrationWarning
        >
          {country.name}
        </option>
      ))}
    </StyledElement>
  );

  return (
    <>
      {label && (
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={id}
          className={clsx('cp_form-label')}
          required={required}
        >
          {label}
        </StyledElement>
      )}
      {focusableInputId ? (
        <FocusableInput elementId={focusableInputId}>{select}</FocusableInput>
      ) : (
        select
      )}
    </>
  );
}
